// The bottom tool dock: a flat segmented tab row (Problems · Output · Script ·
// Certification · Cost · Lockfile) with per-tab count badges, a compact
// gate verdict chip + a collapse control on the right, and a dense scrollable
// body that switches on the active tab. Each tab owns its own empty state.
// Parameterless, binds to the store.

import { PanelBottomClose } from "lucide-react";
import { useIde } from "../store";
import type { BottomTab } from "../store";
import type { CompileResult, Diagnostic, Gate } from "../types";
import { ProblemsPanel } from "./ProblemsPanel";
import { OutputPanel } from "./OutputPanel";
import { TaptreePanel } from "./TaptreePanel";
import { CertificationPanel } from "./CertificationPanel";
import { CostPanel } from "./CostPanel";
import { LockfilePanel } from "./LockfilePanel";
import "./panels.css";

type BadgeTone = "neutral" | "error" | "warning" | "success";

interface BadgeSpec {
  count: number | null;
  tone: BadgeTone;
}

interface TabSpec {
  id: BottomTab;
  label: string;
}

const TABS: TabSpec[] = [
  { id: "problems", label: "Problems" },
  { id: "output", label: "Output" },
  { id: "script", label: "Script" },
  { id: "certification", label: "Certification" },
  { id: "cost", label: "Cost" },
  { id: "lockfile", label: "Lockfile" },
];

/** Per-tab badge: a count where one is meaningful, colored by severity/verdict.
 *  Neutral counts stay quiet (--bg-3); a colored tone is reserved for problems
 *  and certification so the row doesn't turn into a wall of accents. */
function badgeFor(
  id: BottomTab,
  diagnostics: Diagnostic[],
  result: CompileResult | null,
): BadgeSpec {
  switch (id) {
    case "problems": {
      const errors = diagnostics.filter((d) => d.severity === "error").length;
      const warnings = diagnostics.filter((d) => d.severity === "warning").length;
      if (errors > 0) return { count: errors + warnings, tone: "error" };
      if (warnings > 0) return { count: warnings, tone: "warning" };
      return { count: null, tone: "neutral" };
    }
    case "script":
      return { count: result?.taproot?.leaves.length ?? result?.leaves?.length ?? null, tone: "neutral" };
    case "certification": {
      const certs = result?.certification;
      if (!certs || certs.length === 0) return { count: null, tone: "neutral" };
      const diverged = certs.some((c) => c.assurance === "divergence");
      const unproven = certs.some((c) => c.assurance === "unproven");
      const tone: BadgeTone = diverged ? "error" : unproven ? "warning" : "success";
      return { count: certs.length, tone };
    }
    case "cost":
      return { count: result?.costs?.length ?? null, tone: "neutral" };
    case "output":
    case "lockfile":
      return { count: null, tone: "neutral" };
  }
}

interface GateFace {
  cls: string;
  label: string;
  title: string;
}

/** The right-aligned verdict chip, the headline state of the last compile. */
function gateFace(gate: Gate, assembled: boolean): GateFace {
  if (gate.divergence) {
    return {
      cls: "pn-gate--divergence",
      label: "Divergence",
      title: "A spend path's certified behavior contradicts the source",
    };
  }
  if (gate.mayProceed && gate.funding) {
    // The gate speaks for certification only. If a later stage failed there is
    // no address, so claiming "safe to fund" beside an error would contradict
    // itself: the paths are proven, the contract is not assembled.
    if (!assembled) {
      return {
        cls: "pn-gate--unproven",
        label: "Not assembled",
        title: "Every spend path is proven, but the contract could not be assembled",
      };
    }
    return {
      cls: "pn-gate--proven",
      label: "Proven",
      title: "Every spend path proven, safe to fund",
    };
  }
  const n = gate.unproven;
  return {
    cls: "pn-gate--unproven",
    label: "Refused",
    title: `${n} unproven ${n === 1 ? "path" : "paths"}, funding refused`,
  };
}

export function BottomPanel() {
  const bottomTab = useIde((s) => s.bottomTab);
  const setBottomTab = useIde((s) => s.setBottomTab);
  const toggleBottom = useIde((s) => s.toggleBottom);
  const diagnostics = useIde((s) => s.diagnostics);
  const result = useIde((s) => s.result);
  const lastCompileMs = useIde((s) => s.lastCompileMs);

  const gate = result?.gate;
  const face = gate ? gateFace(gate, result?.ok === true) : null;

  return (
    <section className="pn" aria-label="Tool panel">
      <div className="pn-tabrow">
        <div className="pn-tabs" role="tablist" aria-label="Results">
          {TABS.map((tab) => {
            const badge = badgeFor(tab.id, diagnostics, result);
            const active = tab.id === bottomTab;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`pn-tab${active ? " is-active" : ""}`}
                onClick={() => setBottomTab(tab.id)}
              >
                <span className="pn-tab-label">{tab.label}</span>
                {badge.count != null && (
                  <span className={`pn-tab-badge pn-tab-badge--${badge.tone}`}>{badge.count}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="pn-tabrow-right">
          {face && lastCompileMs != null && (
            <span className="pn-tabrow-ms" title="Last compile time">
              {lastCompileMs} ms
            </span>
          )}
          {face && (
            <>
              <span className={`pn-gate ${face.cls}`} title={face.title}>
                <span className="pn-gate-dot" aria-hidden="true" />
                {face.label}
              </span>
              <span className="pn-tabrow-sep" aria-hidden="true" />
            </>
          )}
          <button
            type="button"
            className="pn-icon-btn"
            onClick={toggleBottom}
            aria-label="Collapse panel"
            title="Collapse panel (⌘J)"
          >
            <PanelBottomClose size={15} strokeWidth={1.85} />
          </button>
        </div>
      </div>

      <div className="pn-body" role="tabpanel">
        {bottomTab === "problems" && <ProblemsPanel />}
        {bottomTab === "output" && <OutputPanel />}
        {bottomTab === "script" && <TaptreePanel />}
        {bottomTab === "certification" && <CertificationPanel />}
        {bottomTab === "cost" && <CostPanel />}
        {bottomTab === "lockfile" && <LockfilePanel />}
      </div>
    </section>
  );
}
